// Commented out because it increases bundle size.
// Have not researched how to conditionally lazy import these polyfills.
// IE is a very low priority.
// IE support
// install react-app-polyfill but this results in a large bundle which affects site performance rating
// import 'react-app-polyfill/ie11';
// import 'react-app-polyfill/stable';
import * as serviceWorker from './serviceWorker';
// import './App/config/googleAnalytics/setupGoogleAnalytics';
import './features/audio/howlerSetup';
import { env } from './App/config/env';
import './App/config/reporting/setupReporting';
import { Helmet } from './helmets/Helmet';
import { ApolloProvider, gql, useQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import { render } from 'react-dom';
import { Router } from 'react-router-dom';
import { Provider as ReduxProvider, useDispatch } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { createBrowserHistory } from 'history';
import qs from 'query-string';
// disabled out because we have disabled usage of recaptcha due to GDPR complications
// https://lithodomosvr.atlassian.net/browse/LVR-3716
// import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { App } from './App/App';
import { getApolloClient } from './App/config/apollo/ApolloClient';
import { ErrorBoundary } from './App/ErrorBoundary';
import { setInitialLanguage } from './features/i18n/setInitialLanguage';
import { createStore } from './store';
import { createDeviceID } from './utils/createDeviceID';
import { setUpdateAvailable } from './store/sw/actions';
import GoogleOptimizeExperimentsLoader from './App/components/GoogleOptimizeExperimentsLoader';
import { AppGlobalStyle } from './style/AppGlobalStyle';
import { ThemeProvider } from './style/styled';
import { ErrorBoundaryMini } from './components/ErrorBoundaryMini';
import { CurrencyAutoSetter } from './features/multiCurrency/components/CurrencyAutoSetter';
import { ASSET_FRAGMENT } from './graphql/fragments/Asset';
import {
  AK_APP_CONFIGURATION,
  AO_APP_CONFIGURATION,
  DEFAULT_APP_CONFIGURATION,
} from './features/whitelabeling/branding/consts';
import {
  EXPLORE_WEB_GetApp as Data,
  EXPLORE_WEB_GetAppVariables as Variables,
} from './__generated__/EXPLORE_WEB_GetApp';
import { getAppConfig } from './utils/getAppConfig';
import { getAppID } from './App/config/getAppID';
import { setAppEnv } from './utils/setAppEnv';
import { ITheme } from './features/whitelabeling/branding/types';
import { setAppID } from './utils/setAppID';
import Tracking from './App/config/tracking/Tracking';
import { useBeforeFirstRender } from './hooks/useBeforeFirstRender';
import { enableFulscreenLoadingOverlay } from './store/app/actions';
import { CodeKeys } from './types';
import { ToursForToursSectionProvider } from './providers';
// Commented out because Google Optimize does not seem to work otherwise. Haven't investigated why.
// import Tracking from './App/config/tracking/Tracking';

createDeviceID();

const history = createBrowserHistory();

// we pass the history because we want to pass it to saga context so that we can use routing
// inside a saga.
const { store, persistor } = createStore(history);

async function renderApp() {
  const { apolloClient } = await loadInitialLibraries();

  render(
    <ErrorBoundary>
      {/* required for routing */}
      <Router history={history}>
        {/* required for redux */}
        <ReduxProvider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            {/* <GoogleReCaptchaProvider
                  useRecaptchaNet
                  reCaptchaKey={env.RECAPTCHA_SITE_KEY}
                > */}
            {/* required for graphql */}
            <ApolloProvider client={apolloClient}>
              <ToursForToursSectionProvider>
                {env.IS_STOCK ||
                history.location.pathname === '/privacy_policy' ||
                history.location.pathname === '/terms_conditions' ||
                history.location.pathname === '/delete_account/request' ? (
                  <AppWithDefaultConfig />
                ) : (
                  <AppWithRemoteConfig />
                )}
              </ToursForToursSectionProvider>
            </ApolloProvider>
            {/* </GoogleReCaptchaProvider> */}
          </PersistGate>
        </ReduxProvider>
      </Router>
    </ErrorBoundary>,
    document.getElementById('root')
  );
}

const GET_APP_QUERY = gql`
  query EXPLORE_WEB_GetApp($input: GetAppInput!) {
    result: getApp(input: $input) {
      id
      appID
      appType
      url
      stylesheet {
        ...AssetFragment
      }
      theme {
        ...AssetFragment
      }
      settings {
        ...AssetFragment
      }
    }
  }

  ${ASSET_FRAGMENT}
`;

type Config = {
  theme: ITheme;
  stylesheet: string;
};

interface AppWithThemeProps {
  stylesheetURL: string;
  theme: ITheme;
}

const AppWithTheme: React.FC<AppWithThemeProps> = ({
  theme,
  stylesheetURL,
}) => {
  return (
    // styled components theme providers. theme can be defined per app (whitelabel app)
    <ThemeProvider theme={theme}>
      <Helmet stylesheetURL={stylesheetURL} />

      {/* Automatically set the currency for a user, based on their IP info */}
      <ErrorBoundaryMini>
        <CurrencyAutoSetter />
      </ErrorBoundaryMini>

      {/* Common styles */}
      <AppGlobalStyle />

      {/* Common styles */}
      {/* <GlobalStyle /> */}

      <ErrorBoundaryMini>
        <GoogleOptimizeExperimentsLoader />
      </ErrorBoundaryMini>

      {/* Main app and routes */}
      <App />

      {/* Commented out because Optimize stopped working due to lazy loading Google Analytics */}
      <Tracking />

      {/* react-toastify integration. DO NOT REMOVE unless ditching the library */}
      <ToastContainer />
    </ThemeProvider>
  );
};

const AppWithDefaultConfig = () => {
  return (
    <AppWithTheme
      theme={
        env.IS_AK
          ? AK_APP_CONFIGURATION.theme
          : env.IS_AO
          ? AO_APP_CONFIGURATION.theme
          : DEFAULT_APP_CONFIGURATION.theme
      }
      stylesheetURL={
        env.IS_AK
          ? AK_APP_CONFIGURATION.stylesheet
          : env.IS_AO
          ? AO_APP_CONFIGURATION.stylesheet
          : DEFAULT_APP_CONFIGURATION.stylesheet
      }
    />
  );
};

const appID = getAppID(env.IS_STOCK, env.IS_RTM, env.IS_AK, env.IS_AO);

console.log('appID:', appID);

const AppWithRemoteConfig = () => {
  const dispatch = useDispatch();

  // Display fullscreen loading overlay on top the entire app,
  // when we are entering with a viewpoint activation link.
  // The activation will occur under the hood,
  // and as soon as we enter the viewpoint,
  // we dispatch disableFulscreenLoadingOverlay()
  useBeforeFirstRender(() => {
    const parsedSearch = qs.parse(window.location.search);
    if (
      parsedSearch?.[CodeKeys.REDEEM_CODE] &&
      window.location.pathname.includes('viewpoint')
    ) {
      dispatch(enableFulscreenLoadingOverlay());
    }
  });

  const [config, setConfig] = useState<Config | null>(null);
  const { data, loading } = useQuery<Data, Variables>(GET_APP_QUERY, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    variables: {
      input: {
        appID,
      },
    },
  });

  const appData = data?.result;

  console.log('appData:', appData);

  // TODO: [LVR-2585]
  useEffect(() => {
    if (
      window.location.href ===
        'https://guide.lithodomos.com/athenian-acropolis' ||
      window.location.href ===
        'https://guide.lithodomos.com/athenian-acropolis-staging' ||
      (window.location.href === 'https://guide.lithodomos.com/' &&
        ['athenian-acropolis', 'athenian-acropolis-staging'].indexOf(appID) >=
          0)
    ) {
      window.location.href =
        'https://discover.lithodomos.com/athenian-acropolis';
    }

    if (
      window.location.href === 'https://guide.lithodomos.com/paestum' ||
      window.location.href === 'https://guide.lithodomos.com/paestum-staging' ||
      (window.location.href === 'https://guide.lithodomos.com/' &&
        ['paestum', 'paestum-staging'].indexOf(appID) >= 0)
    ) {
      window.location.href = 'https://discover.lithodomos.com/paestum';
    }
    if (
      window.location.href === 'https://guide.lithodomos.com/medina-azahara' ||
      window.location.href ===
        'https://guide.lithodomos.com/medina-azahara-staging' ||
      (window.location.href === 'https://guide.lithodomos.com/' &&
        ['medina-azahara', 'medina-azahara-staging'].indexOf(appID) >= 0)
    ) {
      window.location.href = 'https://discover.lithodomos.com/medina-azahara';
    }

    if (
      window.location.href === 'https://guide.lithodomos.com/london' ||
      window.location.href === 'https://guide.lithodomos.com/london-staging' ||
      (window.location.href === 'https://guide.lithodomos.com/' &&
        ['london', 'london-staging'].indexOf(appID) >= 0)
    ) {
      window.location.href = 'https://discover.lithodomos.com/london';
    }
    if (
      window.location.href === 'https://guide.lithodomos.com/malaga' ||
      window.location.href === 'https://guide.lithodomos.com/malaga-staging' ||
      (window.location.href === 'https://guide.lithodomos.com/' &&
        ['malaga', 'malaga-staging'].indexOf(appID) >= 0)
    ) {
      window.location.href = 'https://discover.lithodomos.com/malaga';
    }
    if (
      window.location.href === 'https://guide.lithodomos.com/barcelona' ||
      window.location.href ===
        'https://guide.lithodomos.com/barcelona-staging' ||
      (window.location.href === 'https://guide.lithodomos.com/' &&
        ['barcelona', 'barcelona-staging'].indexOf(appID) >= 0)
    ) {
      window.location.href = 'https://discover.lithodomos.com/barcelona';
    }
    // if the app is invalid, redirect to ancient world
    if (!loading && !appData) {
      window.location.href = 'https://ancient-world.co';
    }

    // doesn't matter if it's still fetching from the network or not,
    // if there is appData, load the config, setAppID and setAppEnv
    if (appData) {
      getAppConfig(appData).then(({ stylesheet, theme, settings }) => {
        console.log('settings:', settings);
        console.log('stylesheet:', stylesheet);
        console.log('theme:', theme);

        setConfig({ stylesheet, theme });
        setAppID(appData.appID);
        setAppEnv(settings);
      });
    }
  }, [appData, loading]);

  // loading spinner similar to the one in the index.html (bouncing logo)
  if (!config) {
    return (
      <div className="full-page-loader-container">
        <div>
          <img src="/apple-touch-icon.png" alt="" />
        </div>
      </div>
    );
  }

  return (
    <AppWithTheme stylesheetURL={config.stylesheet} theme={config.theme} />
  );
};

async function loadInitialLibraries() {
  const [apolloClient, language] = await Promise.all([
    // getApolloClient is async because rehydrating apollo cache to idb storage is async
    getApolloClient(),
    // setInitialLanguage is async because setLanguage fn called within it is async
    setInitialLanguage(),
  ]);

  return { apolloClient, language };
}

renderApp();

// service worker registration is required for offline support.
if (env.ENABLE_SERVICE_WORKER) {
  serviceWorker.register({
    onUpdate: (registration: ServiceWorkerRegistration) => {
      console.log('Service worker: update available.');

      store.dispatch(setUpdateAvailable({ available: true }));
    },
    onSuccess: (registration: ServiceWorkerRegistration) => {
      console.log('Service worker: successfully registered.');
    },
  });
} else {
  serviceWorker.unregister();
}
